import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation, AnimatePresence, useInView } from 'framer-motion';
import { 
  ChevronDownIcon, 
  LocationMarkerIcon,
  ArrowRightIcon,
} from '@heroicons/react/outline';

// Composant principal de la Landing Page
const ContactDisplay = () => {

    // Références pour l'animation au scroll
    const heroRef = useRef(null);
    const statsRef = useRef(null);
    const featuresRef = useRef(null);
    const platformRef = useRef(null);
    const screensRef = useRef(null);
    const partnersRef = useRef(null);
    const testimonialsRef = useRef(null);
    
    // InView Hooks pour déclencher les animations
    const isHeroInView = useInView(heroRef, { once: false });
    const isStatsInView = useInView(statsRef, { once: false });
    const isFeaturesInView = useInView(featuresRef, { once: false });
    const isPlatformInView = useInView(platformRef, { once: false });
    const isScreensInView = useInView(screensRef, { once: false });
    const isPartnersInView = useInView(partnersRef, { once: false });
    const isTestimonialsInView = useInView(testimonialsRef, { once: false });
    
    // Animations contrôlées
    const heroControls = useAnimation();
    const statsControls = useAnimation();
    const featuresControls = useAnimation();
    const platformControls = useAnimation();
    const screensControls = useAnimation();
    const partnersControls = useAnimation();
    const testimonialsControls = useAnimation();
    // Effet pour les animations au scroll
    useEffect(() => {
      if (isHeroInView) heroControls.start("visible");
      if (isStatsInView) statsControls.start("visible");
      if (isFeaturesInView) featuresControls.start("visible");
      if (isPlatformInView) platformControls.start("visible");
      if (isScreensInView) screensControls.start("visible");
      if (isPartnersInView) partnersControls.start("visible");
      if (isTestimonialsInView) testimonialsControls.start("visible");
    }, [
      isHeroInView, isStatsInView, isFeaturesInView, isPlatformInView, 
      isScreensInView, isPartnersInView, isTestimonialsInView,
      heroControls, statsControls, featuresControls, platformControls, 
      screensControls, partnersControls, testimonialsControls
    ]);

  
    // Effets d'animation Framer Motion
    const fadeInUp = {
      hidden: { opacity: 0, y: 30 },
      visible: { 
        opacity: 1, 
        y: 0,
        transition: { 
          duration: 0.8,
          ease: "easeOut"
        }
      }
    };
    
    const staggerContainer = {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.1
        }
      }
    };
    
    const scaleIn = {
      hidden: { opacity: 0, scale: 0.8 },
      visible: { 
        opacity: 1, 
        scale: 1,
        transition: { duration: 0.6 }
      }
    };
    
    const slideInLeft = {
      hidden: { opacity: 0, x: -50 },
      visible: { 
        opacity: 1, 
        x: 0,
        transition: { duration: 0.7 }
      }
    };
    
    const slideInRight = {
      hidden: { opacity: 0, x: 50 },
      visible: { 
        opacity: 1, 
        x: 0,
        transition: { duration: 0.7 }
      }
    };

   return (
      <div className="min-h-screen bg-gray-50">

        {/* Section Contact - Redesign moderne inspiré des formulaires de contact premium */}
              <section className="py-24 px-4 bg-gray-50" id="contact">
                <div className="max-w-7xl mx-auto">
                  <motion.div 
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                  >
                    <div className="inline-block bg-orange-100 text-[#E84E0E] text-sm font-semibold px-4 py-1 rounded-full mb-4">
                      CONTACTEZ-NOUS
                    </div>
                    <h2 className="text-3xl md:text-5xl font-bold mt-2 mb-4 text-gray-900">
                      Des questions ? Parlons-en
                    </h2>
                    <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                      Notre équipe est disponible pour répondre à toutes vos questions et vous accompagner dans votre projet
                    </p>
                  </motion.div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    {/* Formulaire de contact */}
                    <motion.div 
                      className="lg:col-span-7 bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-100"
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8 }}
                    >
                      <div className="p-8 md:p-10">
                        <h3 className="text-2xl font-bold mb-8 text-gray-900 flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-[#E84E0E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                          </svg>
                          Envoyez-nous un message
                        </h3>
                        
                        <form>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div className="space-y-2">
                              <label className="block text-gray-700 font-medium">Nom</label>
                              <input 
                                type="text" 
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E84E0E] focus:border-transparent transition-all"
                                placeholder="Votre nom"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="block text-gray-700 font-medium">Email</label>
                              <input 
                                type="email" 
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E84E0E] focus:border-transparent transition-all"
                                placeholder="Votre email"
                              />
                            </div>
                          </div>
                          
                          <div className="mb-6 space-y-2">
                            <label className="block text-gray-700 font-medium">Sujet</label>
                            <div className="relative">
                              <select className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E84E0E] focus:border-transparent transition-all appearance-none bg-white">
                                <option>Demande d'information</option>
                                <option>Devenir annonceur</option>
                                <option>Devenir partenaire</option>
                                <option>Support technique</option>
                                <option>Autre sujet</option>
                              </select>
                              <ChevronDownIcon className="h-5 w-5 text-gray-400 absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                            </div>
                          </div>
                          
                          <div className="mb-8 space-y-2">
                            <label className="block text-gray-700 font-medium">Message</label>
                            <textarea 
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E84E0E] focus:border-transparent transition-all h-32 resize-none"
                              placeholder="Votre message"
                            ></textarea>
                          </div>
                          
                          <motion.button 
                            type="submit"
                            className="bg-gradient-to-r from-[#E84E0E] to-orange-600 hover:from-[#E84E0E] hover:to-[#E84E0E] text-white font-bold py-3 px-8 rounded-xl shadow-lg transition duration-300 ease-in-out w-full md:w-auto flex items-center justify-center"
                            whileHover={{ scale: 1.02, boxShadow: "0 10px 25px -5px rgba(232, 78, 14, 0.4)" }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <span>Envoyer le message</span>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                          </motion.button>
                        </form>
                      </div>
                    </motion.div>
                    
                    {/* Informations de contact */}
                    <motion.div 
                      className="lg:col-span-5 flex flex-col justify-between"
                      initial={{ opacity: 0, x: 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                    >
                      <div>
                        <h3 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-[#E84E0E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Informations de contact
                        </h3>
                        <p className="text-lg text-gray-600 mb-8">
                          N'hésitez pas à nous contacter directement ou à passer dans nos bureaux pour en savoir plus sur notre plateforme DOOH.
                        </p>
                        
                        <div className="space-y-6 mb-10">
                          <div className="bg-white rounded-xl p-6 shadow-lg">
                            <div className="space-y-6">
                              {[
                                {
                                  icon: <LocationMarkerIcon className="h-6 w-6 text-[#E84E0E]" />,
                                  title: "Adresse",
                                  description: "Douala, Cameroun",
                                  action: "Voir sur la carte",
                                  actionIcon: <ArrowRightIcon className="h-4 w-4 ml-1" />
                                },
                                {
                                  icon: <svg className="h-6 w-6 text-[#E84E0E]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>,
                                  title: "Téléphone",
                                  description: "+237 233 42 XX XX",
                                  action: "Appeler maintenant",
                                  actionIcon: <svg className="h-4 w-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                                },
                                {
                                  icon: <svg className="h-6 w-6 text-[#E84E0E]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
                                  title: "Email",
                                  description: "info@accentmedia.com",
                                  action: "Envoyer un email",
                                  actionIcon: <svg className="h-4 w-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                }
                              ].map((info, index) => (
                                <motion.div 
                                  key={index} 
                                  className="flex items-start"
                                  initial={{ opacity: 0, y: 10 }}
                                  whileInView={{ opacity: 1, y: 0 }}
                                  transition={{ duration: 0.5, delay: 0.3 + index * 0.2 }}
                                >
                                  <div className="mr-4 p-3 bg-orange-100 rounded-xl">{info.icon}</div>
                                  <div className="flex-1">
                                    <h4 className="font-bold text-lg text-gray-900">{info.title}</h4>
                                    <p className="text-gray-600 mb-2">{info.description}</p>
                                    <a href="#" className="text-[#E84E0E] text-sm font-medium flex items-center hover:underline">
                                      {info.action}
                                      {info.actionIcon}
                                    </a>
                                  </div>
                                </motion.div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="text-xl font-bold mb-4 text-gray-900 flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#E84E0E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                          </svg>
                          Suivez-nous
                        </h3>
                        
                        <div className="flex space-x-4">
                          {['facebook', 'twitter', 'instagram', 'linkedin'].map((social, index) => (
                            <motion.a 
                              key={social} 
                              href={`#${social}`} 
                              className="h-12 w-12 rounded-xl bg-white flex items-center justify-center text-gray-600 hover:bg-[#E84E0E] hover:text-white transition-colors duration-300 shadow-md"
                              whileHover={{ y: -5, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }}
                              initial={{ opacity: 0, y: 10 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3, delay: 0.5 + index * 0.1 }}
                            >
                              <span className="sr-only">{social}</span>
                              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                {social === 'facebook' && <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />}
                                {social === 'twitter' && <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />}
                                {social === 'instagram' && <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />}
                                {social === 'linkedin' && <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" />}
                              </svg>
                            </motion.a>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </section>

              <footer className="bg-gray-900 text-white">
        {/* Footer Principal */}
        <div className="pt-20 pb-12 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-10">
              {/* Section Marque */}
              <div className="md:col-span-2">
                <div className="flex items-center mb-6">
                  <div className="h-12 w-12 bg-[#E84E0E] rounded-xl flex items-center justify-center mr-3 shadow-lg">
                    <span className="text-white font-bold text-2xl">A</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">ACCENT MEDIA</h3>
                    <p className="text-gray-400 text-sm">Digital Out Of Home</p>
                  </div>
                </div>
                <p className="text-gray-400 mb-8 pr-8">
                  L'innovation au service de la communication. Notre plateforme révolutionne la façon dont vous réservez et gérez vos campagnes d'affichage digital sur notre réseau d'écrans stratégiquement placés.
                </p>
                <div className="flex space-x-4">
                  {['facebook', 'twitter', 'instagram', 'linkedin'].map((social) => (
                    <a 
                      key={social} 
                      href={`#${social}`} 
                      className="h-10 w-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-[#E84E0E] hover:text-white transition-colors duration-300"
                      aria-label={social}
                    >
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        {social === 'facebook' && <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />}
                        {social === 'twitter' && <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />}
                        {social === 'instagram' && <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />}
                        {social === 'linkedin' && <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" />}
                      </svg>
                    </a>
                  ))}
                </div>
              </div>
              
              {/* Sections de liens */}
              <div className="md:col-span-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
                <div>
                  <h4 className="font-bold text-lg mb-5">Plateforme</h4>
                  <ul className="space-y-3 text-gray-400">
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Fonctionnalités</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Tarifs</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Réservations</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">API</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Tableau de bord</a></li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-5">Réseau</h4>
                  <ul className="space-y-3 text-gray-400">
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Carte des écrans</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Audience</a></li>
                    <li><a href="#partenariat" className="hover:text-[#E84E0E] transition-colors duration-200">Devenir partenaire</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Formats supportés</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Emplacements premium</a></li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-5">Ressources</h4>
                  <ul className="space-y-3 text-gray-400">
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Centre d'aide</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Blog</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Études de cas</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Galerie créative</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Tendances DOOH</a></li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-5">Entreprise</h4>
                  <ul className="space-y-3 text-gray-400">
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">À propos</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Équipe</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Carrières</a></li>
                    <li><a href="#" className="hover:text-[#E84E0E] transition-colors duration-200">Presse</a></li>
                    <li><a href="#contact" className="hover:text-[#E84E0E] transition-colors duration-200">Contact</a></li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Section Newsletter */}
            <div className="mt-12 pt-8 border-t border-gray-800">
              <div className="flex flex-col md:flex-row justify-between items-center">
                <div className="mb-6 md:mb-0">
                  <h4 className="font-bold text-lg mb-2">Restez informé</h4>
                  <p className="text-gray-400">Recevez nos actualités et offres spéciales</p>
                </div>
                <div className="w-full md:w-auto">
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0">
                    <input 
                      type="email" 
                      placeholder="Votre email" 
                      className="px-4 py-3 bg-gray-800 text-white rounded-lg sm:rounded-r-none focus:outline-none focus:ring-2 focus:ring-[#E84E0E] w-full md:w-64"
                    />
                    <button className="bg-[#E84E0E] hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-lg sm:rounded-l-none transition-colors duration-300">
                      S'abonner
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Section Copyright */}
        <div className="py-6 bg-black">
          <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-500 text-sm mb-4 md:mb-0">
              © 2025 Accent Media & Technology SA. Tous droits réservés.
            </div>
            <div className="flex flex-wrap justify-center space-x-6 text-sm">
              <a href="#" className="text-gray-500 hover:text-gray-400 transition-colors duration-200 mb-2 md:mb-0">Confidentialité</a>
              <a href="#" className="text-gray-500 hover:text-gray-400 transition-colors duration-200 mb-2 md:mb-0">Conditions d'utilisation</a>
              <a href="#" className="text-gray-500 hover:text-gray-400 transition-colors duration-200 mb-2 md:mb-0">Mentions légales</a>
              <a href="#" className="text-gray-500 hover:text-gray-400 transition-colors duration-200 mb-2 md:mb-0">Cookies</a>
            </div>
          </div>
        </div>
      </footer>
        
        {/* Autres sections seront ajoutées ici */}
      </div>
    );
  };
  
  export default ContactDisplay;